import React from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  type?: 'confirm' | 'success' | 'danger';
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  type = 'confirm'
}) => {
  if (!isOpen) return null;

  const isSuccess = type === 'success';

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100 relative z-[10000]">
        
        {/* Header */}
        <div className={`flex justify-between items-center p-5 border-b border-slate-100 ${isSuccess ? 'bg-emerald-50' : ''}`}>
          <div className="flex items-center gap-3">
            {type === 'danger' && <div className="bg-red-100 p-2 rounded-full text-red-600"><AlertTriangle size={20} /></div>}
            {type === 'success' && <div className="bg-emerald-100 p-2 rounded-full text-emerald-600"><CheckCircle size={20} /></div>}
            <h3 className={`font-bold text-lg ${isSuccess ? 'text-emerald-900' : 'text-slate-900'}`}>{title}</h3>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-slate-100 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-6">
          <p className="text-slate-600 leading-relaxed text-base">{message}</p>
        </div>

        {/* Footer */}
        <div className="p-5 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
          {!isSuccess && (
            <button 
                onClick={onCancel}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white hover:shadow-sm hover:text-slate-900 border border-transparent hover:border-slate-200 rounded-lg transition-all"
            >
                {cancelText}
            </button>
          )}
          
          {onConfirm ? (
              <button 
                onClick={onConfirm}
                className={`px-5 py-2.5 text-sm font-bold text-white rounded-lg shadow-md transition-all active:scale-95 flex items-center gap-2 ${
                isDestructive || type === 'danger'
                    ? 'bg-red-600 hover:bg-red-700 shadow-red-200' 
                    : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'
                }`}
            >
                {confirmText}
            </button>
          ) : (
            <button 
                onClick={onCancel}
                className="px-5 py-2.5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-md shadow-emerald-200 transition-all active:scale-95"
            >
                Close
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
